import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Clock, WarningCircle } from '@phosphor-icons/react';

import { getInvoiceSummary, type InvoiceSummary } from '@/api/billing';
import { formatMoney } from '@/lib/money';

function invoiceCount(s: InvoiceSummary): number {
  return (
    s.count_draft +
    s.count_issued +
    s.count_partially_paid +
    s.count_paid +
    s.count_overdue +
    s.count_void
  );
}

// InvoiceSummaryPanel is the at-a-glance dashboard at the top of the Fakturor
// page: money received vs. still owed, the collection rate, and a status
// breakdown. Charts are plain CSS bars (no charting dependency) so they inherit
// the design tokens and stay responsive. Hidden until the org has invoices.
export function InvoiceSummaryPanel({ slug }: { slug: string }) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const query = useQuery({
    queryKey: ['invoice-summary', slug],
    queryFn: () => getInvoiceSummary(token, slug),
    enabled: !!token && !!slug,
  });

  const s = query.data ?? null;
  if (!s) return null;
  if (invoiceCount(s) === 0) return null; // no invoices yet — hide the panel

  const money = (minor: number) => formatMoney(minor, s.currency, i18n.language);
  const collectionRate = s.expected_minor > 0 ? Math.round((s.collected_minor / s.expected_minor) * 100) : 0;

  // Unpaid = issued + partially paid (overdue is broken out as its own,
  // attention-worthy bucket).
  const unpaid = s.count_issued + s.count_partially_paid;
  const segments = [
    { key: 'paid', count: s.count_paid, className: 'bg-primary' },
    { key: 'unpaid', count: unpaid, className: 'bg-orange' },
    { key: 'overdue', count: s.count_overdue, className: 'bg-destructive' },
    { key: 'draft', count: s.count_draft, className: 'bg-aqua' },
    { key: 'void', count: s.count_void, className: 'bg-foreground/45' },
  ].filter((seg) => seg.count > 0);
  const statusTotal = segments.reduce((acc, seg) => acc + seg.count, 0);

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          icon={<CheckCircle size={20} weight="fill" className="text-primary" />}
          label={t('billing.invoices.summary.collected', { defaultValue: 'Collected' })}
          value={money(s.collected_minor)}
        />
        <StatCard
          icon={<Clock size={20} weight="bold" className="text-foreground/60" />}
          label={t('billing.invoices.summary.outstanding', { defaultValue: 'Outstanding' })}
          value={money(s.outstanding_minor)}
        />
        <StatCard
          icon={<WarningCircle size={20} weight="bold" className="text-foreground/60" />}
          label={t('billing.invoices.summary.expected', { defaultValue: 'Expected if all pay' })}
          value={money(s.expected_minor)}
        />
      </div>

      {/* Collection progress: collected vs. expected. */}
      {s.expected_minor > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t('billing.invoices.summary.collectionRate', { defaultValue: 'Collected so far' })}</span>
            <span className="font-heading font-semibold text-foreground">{collectionRate}%</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-pill bg-muted">
            <div
              className="h-full rounded-pill bg-primary transition-[width] duration-500 motion-reduce:transition-none"
              style={{ width: `${collectionRate}%` }}
            />
          </div>
        </div>
      )}

      {/* Status breakdown: one stacked bar by count + a legend with counts. */}
      {statusTotal > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex h-2.5 w-full overflow-hidden rounded-pill bg-muted">
            {segments.map((seg) => (
              <div
                key={seg.key}
                className={seg.className}
                style={{ width: `${(seg.count / statusTotal) * 100}%` }}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <LegendItem dot="bg-primary" label={t('billing.invoices.summary.status.paid', { defaultValue: 'Paid' })} count={s.count_paid} />
            <LegendItem dot="bg-orange" label={t('billing.invoices.summary.status.unpaid', { defaultValue: 'Unpaid' })} count={unpaid} />
            <LegendItem dot="bg-destructive" label={t('billing.invoices.summary.status.overdue', { defaultValue: 'Overdue' })} count={s.count_overdue} />
            <LegendItem dot="bg-aqua" label={t('billing.invoices.summary.status.draft', { defaultValue: 'Draft' })} count={s.count_draft} />
            <LegendItem dot="bg-foreground/45" label={t('billing.invoices.summary.status.void', { defaultValue: 'Void' })} count={s.count_void} />
          </div>
        </div>
      )}
    </section>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-border/60 bg-background p-3">
      <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="font-heading text-xl font-bold tabular-nums tracking-tight">{value}</span>
    </div>
  );
}

function LegendItem({ dot, label, count }: { dot: string; label: string; count: number }) {
  if (count === 0) return null;
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      <span className={'h-2.5 w-2.5 shrink-0 rounded-full ' + dot} aria-hidden />
      {label}
      <span className="font-semibold tabular-nums text-foreground">{count}</span>
    </span>
  );
}

